// @copyright  2006 - 2026 West Indies Software Engineering Company Limited. All rights reserved.

/**
 * Warehouse locale strings (warehouse-transfers-and-assignment wp-12).
 * British English throughout. A 409 (business refusal) and a 422 (rejected
 * input) carry different messages deliberately — they mean different things
 * and must never render the same.
 */
export default {
    '$dir': 'ltr',

    'warehouse.page.title': 'Warehouses',
    'warehouse.page.search_results': 'Search Results',
    'warehouse.page.create.title': 'Create New Warehouse',
    'warehouse.page.search.title': 'Find Warehouses',
    'warehouse.page.edit.title': 'Edit Warehouse',
    'warehouse.page.archive.title': 'Archived Warehouses',
    'warehouse.archive.empty': 'No warehouses have been archived.',
    'warehouse.page.all': 'All',

    'warehouse.page.stock.title': 'Stock by Warehouse',
    'warehouse.page.locations.title': 'Locations',
    'warehouse.page.transfers.title': 'Stock Transfers',
    'warehouse.page.transfers.create.title': 'Transfer Stock',
    'warehouse.page.members.title': 'Assigned Operators',

    'warehouse.button.cancel': 'Cancel',
    'warehouse.button.select': 'Select',
    'warehouse.button.find': 'Find Warehouses',
    'warehouse.button.search': 'Apply',
    'warehouse.button.create': 'Save Warehouse',
    'warehouse.button.new': 'New Warehouse',
    'warehouse.button.save': 'Save Changes',
    'warehouse.button.edit': 'Edit',
    'warehouse.button.archive': 'Archive',
    'warehouse.button.restore': 'Restore',
    'warehouse.button.reset': 'Reset',
    'warehouse.button.assign_operator': 'Assign Operator',
    'warehouse.button.remove_operator': 'Remove',
    'warehouse.button.set_primary': 'Set as Active',
    'warehouse.button.transfer_stock': 'Transfer Stock',
    'warehouse.button.print_transfer': 'Print Transfer Note',
    'warehouse.button.add_location': 'Add Location',
    'warehouse.button.rename_location': 'Rename',
    'warehouse.button.make_default': 'Make Default',
    'warehouse.button.delete_location': 'Delete',
    'warehouse.button.save_location': 'Save Location',

    'warehouse.placeholder.search': 'Search warehouses',

    'warehouse.link.create_first_warehouse': 'Click here to create your first warehouse',
    'warehouse.link.create_first_transfer': 'Click here to record your first stock transfer',
    'warehouse.link.warehouses': ' Return to Warehouses',
    'warehouse.link.retry': 'Click here to retry',

    'warehouse.checkbox.all': 'Check All',
    'warehouse.checkbox.actions_label': 'with selected',

    'warehouse.label.warehouse_number': 'Warehouse Number',
    'warehouse.label.warehouse_name': 'Warehouse Name',
    'warehouse.label.warehouse': 'Warehouse',
    'warehouse.label.warehouse_description': 'Description',
    'warehouse.label.warehouse_type_name': 'Type',
    'warehouse.label.address_line_1': 'Address Line 1',
    'warehouse.label.address_line_2': 'Address Line 2',
    'warehouse.label.province': 'Parish / Province',
    'warehouse.label.postal_code': 'Postal Code',
    'warehouse.label.country_name': 'Country',
    'warehouse.label.status': 'Status',
    'warehouse.label.created_at': 'Created At',
    'warehouse.label.updated_at': 'Updated At',

    'warehouse.label.operator': 'Operator',
    'warehouse.label.operator_warehouses': 'Warehouses',
    'warehouse.members.empty':
        'No operators are assigned to this warehouse yet. Assign one so their stock movements land here.',
    'warehouse.members.primary_elsewhere': 'Active: {warehouse}',
    'warehouse.placeholder.choose_operator': 'Choose an operator',
    'warehouse.label.replacement_warehouse': 'New active warehouse',
    'warehouse.placeholder.choose_replacement_warehouse': 'Choose a warehouse',
    'warehouse.label.location_code': 'Code',
    'warehouse.label.location_name': 'Name',
    'warehouse.label.location_default': 'Default',
    'warehouse.label.location_units': 'Units',
    'warehouse.locations.empty':
        'This warehouse has no locations yet. Every warehouse normally carries at least its Default location — add one so stock has somewhere to land.',

    'warehouse.label.primary_warehouse': 'Primary Warehouse',
    'warehouse.label.is_primary': 'Active',
    'warehouse.label.assigned_warehouse': 'Assigned Warehouse',

    'warehouse.label.item_name': 'Item',
    'warehouse.label.all_warehouses': 'All warehouses',
    'warehouse.stock.by_warehouse': 'By warehouse',
    'warehouse.stock.by_item': 'By item',
    'warehouse.stock.all_items': 'All items',
    'warehouse.stock.unaccounted_for': '{units} unaccounted for',
    'warehouse.stock.no_rows_for_item':
        'No warehouse holds a stock record for this item, yet the global counter shows {units}.',
    'warehouse.label.units_on_hand': 'Units On Hand',
    'warehouse.label.global_units_on_hand': 'Global Units On Hand',
    'warehouse.label.warehouse_units_on_hand': 'Units Across Warehouses',
    'warehouse.label.reconciles': 'Counters Agree',
    'warehouse.stock.reconciliation': 'Reconciliation',
    'warehouse.label.discrepancy': 'Discrepancy',
    'warehouse.label.no_stock_record': 'No stock record',
    'warehouse.label.archived': 'Archived',

    'warehouse.label.source_warehouse': 'From Warehouse',
    'warehouse.label.destination_warehouse': 'To Warehouse',
    'warehouse.label.transfer_units': 'Quantity to Transfer',
    'warehouse.label.source_on_hand': 'Available at Source',
    'warehouse.label.destination_after': 'At Destination After Transfer',
    'warehouse.label.reference': 'Reference',
    'warehouse.label.transferred_at': 'Transferred At',
    'warehouse.label.transfer_date_from': 'From Date',
    'warehouse.label.transfer_date_to': 'To Date',
    'warehouse.placeholder.choose_warehouse': 'Choose a warehouse',
    'warehouse.placeholder.choose_item': 'Choose an item',
    'warehouse.button.transfer': 'Transfer',
    'warehouse.button.transfer_units': 'Transfer {units} units',
    'warehouse.modal.title.confirm_transfer': 'Confirm Stock Transfer',
    'warehouse.transfers.after_preview':
        'After this transfer: {source} {sourceAfter} · {destination} {destinationAfter}',
    'warehouse.transfers.cannot_be_undone':
        'This cannot be undone — reverse it with another transfer.',
    'warehouse.transfers.only_available': 'Only {units} available at {warehouse}.',
    'warehouse.transfers.no_stock_record_at_source':
        'There is no stock record for this item at {warehouse}, so it cannot be transferred from there.',
    'warehouse.transfers.empty': 'No stock transfers have been recorded yet.',
    'warehouse.transfers.no_destination_warehouse':
        'This organisation has only one live warehouse, so there is nowhere to transfer stock to. Create a second warehouse first.',

    'warehouse.indicator.selling_from': 'Selling from',
    'warehouse.indicator.assigned_note': '(your assigned warehouse)',
    'warehouse.indicator.no_assignment':
        'You are not assigned to a warehouse, so stock cannot be moved. Ask an administrator to assign you to one.',
    'warehouse.indicator.archived_assignment':
        'Every warehouse you are assigned to is archived, so stock cannot be moved. Ask an administrator to assign you to a live warehouse, or restore one of yours.',
    'warehouse.indicator.no_warehouse': 'No warehouse is configured for your organisation.',
    'warehouse.indicator.on_hand_here': '{units} on hand at {warehouse}',
    'warehouse.indicator.on_hand_unavailable': 'On-hand figure unavailable',

    'warehouse.status.live': 'Live',
    'warehouse.status.archived': 'Archived',

    'warehouse.sort.field_label': 'Field',
    'warehouse.sort.direction_label': 'Direction',
    'warehouse.sort.direction_ascending': 'Ascending',
    'warehouse.sort.direction_descending': 'Descending',

    'warehouse.modal.title.warehouse_archive': 'Archive Warehouse',
    'warehouse.modal.title.warehouse_restore': 'Restore Warehouse',
    'warehouse.modal.title.operator_remove': 'Remove Operator Assignment',
    'warehouse.modal.message.warehouse_archive':
        'Are you sure that you want to archive this warehouse? | Are you sure that you want to archive these warehouses?',
    'warehouse.modal.message.warehouse_restore':
        'Are you sure that you want to restore this warehouse? | Are you sure that you want to restore these warehouses?',
    'warehouse.modal.message.operator_remove':
        'Are you sure that you want to remove this operator from the warehouse?',
    'warehouse.modal.message.operator_remove_only':
        'This is the operator’s only warehouse, and every operator needs one to move stock. Assign them to another warehouse before removing this one.',
    'warehouse.modal.message.operator_remove_active':
        'This is the operator’s active warehouse. Choose the warehouse they should work from instead.',
    'warehouse.modal.title.location_delete': 'Delete Location',
    'warehouse.modal.message.location_delete':
        'Are you sure that you want to delete this location?',

    'warehouse.success.message.warehouse_created': 'Your warehouse has been created',
    'warehouse.success.message.warehouse_updated': 'Your warehouse has been updated',
    'warehouse.success.message.warehouse_archived': 'Your warehouse has been archived | All selected warehouses were archived',
    'warehouse.success.message.warehouse_restored': 'Your warehouse has been restored | All selected warehouses were restored',
    'warehouse.success.message.operator_assigned': 'The operator has been assigned to the warehouse',
    'warehouse.success.message.operator_removed': 'The operator assignment has been removed',
    'warehouse.success.message.primary_set': 'The operator’s active warehouse has been updated',
    'warehouse.success.message.transfer_created': 'The stock transfer has been recorded',
    'warehouse.success.message.location_created': 'The location has been added',
    'warehouse.success.message.location_updated': 'The location has been updated',
    'warehouse.success.message.location_deleted': 'The location has been deleted',
    'warehouse.success.message.location_default_set': 'The default location has been updated',

    'warehouse.error.message.error_loading_page':
        'Sorry, there was an error loading the warehouse. | Sorry, there was an error loading warehouses.',
    'warehouse.error.message.request_failed': 'Sorry, the request could not be completed. Please try again.',
    'warehouse.error.message.validation': 'Some of the values entered are not valid. Please correct them and try again.',
    'warehouse.error.message.conflict': 'That action conflicts with the current state of the warehouse, so nothing was changed.',
    'warehouse.error.message.archive_holds_stock':
        'This warehouse still holds stock. Transfer its stock out before archiving it.',
    'warehouse.error.message.archive_last_warehouse':
        'The last remaining warehouse cannot be archived.',
    'warehouse.error.message.no_assigned_warehouse':
        'This user is not assigned to a warehouse.',
    'warehouse.error.message.insufficient_units':
        'The source warehouse does not hold enough units for this transfer.',
    'warehouse.error.message.duplicate_location_code':
        'That code is already used by another location in this warehouse. Choose a different code.',
    'warehouse.error.message.delete_location_holds_stock':
        'This location still holds stock. Move the stock out before deleting it.',
    'warehouse.error.message.delete_default_location':
        'This is the default location, where incoming stock lands. Make another location the default before deleting this one.',
    'warehouse.error.message.insufficient_stock_reassign':
        '{warehouse} holds {held} units. Reduce the quantity or ask a manager to reassign your warehouse.',
    'warehouse.error.message.same_warehouse':
        'The source and destination warehouses must be different.',
    'warehouse.error.message.transfer_archived_warehouse':
        'Stock cannot move into or out of an archived warehouse. Restore it first.',
};
